import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Client } from "../../api-client";
import { TABLE_ROWS_COUNT } from "../../../shared/constants";
import {
  API_CS_AI_CONFIG,
  API_CS_BATCH_UPLOAD_CANDIDATE,
  API_CS_BULK_UPLOAD_JD,
  API_CS_CREATE_JD,
  API_CS_DELETE_CANDIDATE,
  API_CS_DELETE_JD,
  API_CS_GET_JD,
  API_CS_JD_SKILLS,
  API_CS_LIST_CANDIDATES,
  API_CS_LIST_JD,
  API_CS_RESCORE,
  API_CS_RESULTS,
  API_CS_RULE,
  API_CS_RULES,
  API_CS_SCREEN,
  API_CS_UPDATE_JD,
  API_CS_UPLOAD_CANDIDATE,
} from "../../../shared/routes/private.routes";

const config = {
  name: "candidateScreening",
};

/* ----------------------------- Job Descriptions ---------------------------- */

export const getJobDescriptionList: any = createAsyncThunk(
  `${config.name}/getJobDescriptionList`,
  async ({ params }: any) => {
    return Client.get(API_CS_LIST_JD, { params });
  }
);

export const getJobDescriptionById: any = createAsyncThunk(
  `${config.name}/getJobDescriptionById`,
  async (id: string) => {
    return Client.get(API_CS_GET_JD + `${id}`);
  }
);

// Resolved must-have / nice-to-have skills for a JD — used to warn before a run
// if the JD has no must-have skills (which produces a meaningless ranking).
export const getJobDescriptionSkills: any = createAsyncThunk(
  `${config.name}/getJobDescriptionSkills`,
  async (id: string) => {
    return Client.get(API_CS_JD_SKILLS(id));
  }
);

export const createJobDescription: any = createAsyncThunk(
  `${config.name}/createJobDescription`,
  async (body: any) => {
    return Client.post(API_CS_CREATE_JD, body);
  }
);

// Bulk JD import — body must be FormData (the CSV file under field "file").
// The api-client returns { status, data } even for 400 (structural errors),
// so the component inspects the response rather than relying on a throw.
export const bulkUploadJobDescriptions: any = createAsyncThunk(
  `${config.name}/bulkUploadJobDescriptions`,
  async (formData: FormData) => {
    return Client.post(API_CS_BULK_UPLOAD_JD, formData);
  }
);

export const updateJobDescription: any = createAsyncThunk(
  `${config.name}/updateJobDescription`,
  async ({ id, body }: { id: string; body: any }) => {
    return Client.put(API_CS_UPDATE_JD + `${id}`, body);
  }
);

export const deleteJobDescription: any = createAsyncThunk(
  `${config.name}/deleteJobDescription`,
  async (id: string) => {
    return Client.delete(API_CS_DELETE_JD + `${id}`);
  }
);

/* -------------------------------- Candidates ------------------------------- */

export const getCandidateList: any = createAsyncThunk(
  `${config.name}/getCandidateList`,
  async ({ params }: any = {}) => {
    return Client.get(API_CS_LIST_CANDIDATES, { params });
  }
);

// Single resume upload — body must be FormData (file + fields). The api-client
// detects FormData and drops the JSON Content-Type so the browser sets boundaries.
export const uploadCandidate: any = createAsyncThunk(
  `${config.name}/uploadCandidate`,
  async (formData: FormData) => {
    return Client.post(API_CS_UPLOAD_CANDIDATE, formData);
  }
);

// Batch resume upload — FormData with multiple files.
export const batchUploadCandidates: any = createAsyncThunk(
  `${config.name}/batchUploadCandidates`,
  async (formData: FormData) => {
    return Client.post(API_CS_BATCH_UPLOAD_CANDIDATE, formData);
  }
);

export const deleteCandidate: any = createAsyncThunk(
  `${config.name}/deleteCandidate`,
  async (id: string) => {
    return Client.delete(API_CS_DELETE_CANDIDATE + `${id}`);
  }
);

/* ------------------------------ Screening / score -------------------------- */

export const runScreening: any = createAsyncThunk(
  `${config.name}/runScreening`,
  async (body: any) => {
    return Client.post(API_CS_SCREEN, body);
  }
);

export const rescoreScreening: any = createAsyncThunk(
  `${config.name}/rescoreScreening`,
  async (body: any) => {
    return Client.post(API_CS_RESCORE, body);
  }
);

export const getScreeningResults: any = createAsyncThunk(
  `${config.name}/getScreeningResults`,
  async ({ jobDescriptionId, params }: any) => {
    return Client.get(API_CS_RESULTS + `${jobDescriptionId}`, { params });
  }
);

/* -------------------------------- Rule engine ------------------------------ */

export const getScreeningRules: any = createAsyncThunk(
  `${config.name}/getScreeningRules`,
  async ({ params }: any = {}) => {
    return Client.get(API_CS_RULES, { params });
  }
);

export const createScreeningRule: any = createAsyncThunk(
  `${config.name}/createScreeningRule`,
  async (body: any) => {
    return Client.post(API_CS_RULES, body);
  }
);

export const updateScreeningRule: any = createAsyncThunk(
  `${config.name}/updateScreeningRule`,
  async ({ id, body }: { id: string; body: any }) => {
    return Client.put(API_CS_RULE + `${id}`, body);
  }
);

export const deleteScreeningRule: any = createAsyncThunk(
  `${config.name}/deleteScreeningRule`,
  async (id: string) => {
    return Client.delete(API_CS_RULE + `${id}`);
  }
);

/* ----------------------------- AI configuration ---------------------------- */

export const getAiConfig: any = createAsyncThunk(
  `${config.name}/getAiConfig`,
  async () => {
    return Client.get(API_CS_AI_CONFIG);
  }
);

export const updateAiConfig: any = createAsyncThunk(
  `${config.name}/updateAiConfig`,
  async (body: any) => {
    return Client.put(API_CS_AI_CONFIG, body);
  }
);

const initialPagination = { page: 0, limit: TABLE_ROWS_COUNT, total: 0 };

export const candidateScreeningReducer = createSlice({
  name: config.name,
  initialState: {
    // Job descriptions
    loadingJd: false as any,
    jdList: [] as any,
    jdPagination: { ...initialPagination },
    // Candidates
    loadingCandidates: false as any,
    candidateList: [] as any,
    candidatePagination: { ...initialPagination },
    // Results
    loadingResults: false as any,
    results: [] as any,
    resultsPagination: { ...initialPagination },
    // Rules
    loadingRules: false as any,
    rules: [] as any,
    rulesPagination: { ...initialPagination },
    // AI config
    loadingAiConfig: false as any,
    aiConfig: null as any,
    // Shared
    screening: false as any,
  },
  reducers: {
    resetCandidateScreening: (state) => {
      state.loadingJd = false;
      state.jdList = [];
      state.jdPagination = { ...initialPagination };
      state.loadingCandidates = false;
      state.candidateList = [];
      state.candidatePagination = { ...initialPagination };
      state.loadingResults = false;
      state.results = [];
      state.resultsPagination = { ...initialPagination };
      state.loadingRules = false;
      state.rules = [];
      state.rulesPagination = { ...initialPagination };
      state.loadingAiConfig = false;
      state.aiConfig = null;
      state.screening = false;
    },
  },
  extraReducers(builder) {
    builder
      // Job descriptions
      .addCase(getJobDescriptionList.pending, (state) => {
        state.loadingJd = true;
        state.jdList = [] as any;
      })
      .addCase(getJobDescriptionList.fulfilled, (state, action) => {
        state.loadingJd = false;
        state.jdList = (action?.payload?.data?.data ?? []) as any;
        state.jdPagination = {
          page: action?.payload?.data?.page,
          limit: action?.payload?.data?.limit,
          total: action?.payload?.data?.total,
        };
      })
      .addCase(getJobDescriptionList.rejected, (state) => {
        state.loadingJd = false;
        state.jdList = [] as any;
      })
      // Candidates
      .addCase(getCandidateList.pending, (state) => {
        state.loadingCandidates = true;
      })
      .addCase(getCandidateList.fulfilled, (state, action) => {
        state.loadingCandidates = false;
        state.candidateList = (action?.payload?.data?.data ?? []) as any;
        state.candidatePagination = {
          page: action?.payload?.data?.page,
          limit: action?.payload?.data?.limit,
          total: action?.payload?.data?.total,
        };
      })
      .addCase(getCandidateList.rejected, (state) => {
        state.loadingCandidates = false;
      })
      // Screening run
      .addCase(runScreening.pending, (state) => {
        state.screening = true;
      })
      .addCase(runScreening.fulfilled, (state) => {
        state.screening = false;
      })
      .addCase(runScreening.rejected, (state) => {
        state.screening = false;
      })
      // Results
      .addCase(getScreeningResults.pending, (state) => {
        state.loadingResults = true;
        state.results = [] as any;
      })
      .addCase(getScreeningResults.fulfilled, (state, action) => {
        state.loadingResults = false;
        state.results = (action?.payload?.data?.data ?? []) as any;
        state.resultsPagination = {
          page: action?.payload?.data?.page,
          limit: action?.payload?.data?.limit,
          total: action?.payload?.data?.total,
        };
      })
      .addCase(getScreeningResults.rejected, (state) => {
        state.loadingResults = false;
        state.results = [] as any;
      })
      // Rules
      .addCase(getScreeningRules.pending, (state) => {
        state.loadingRules = true;
        state.rules = [] as any;
      })
      .addCase(getScreeningRules.fulfilled, (state, action) => {
        state.loadingRules = false;
        state.rules = (action?.payload?.data?.data ?? []) as any;
        state.rulesPagination = {
          page: action?.payload?.data?.page,
          limit: action?.payload?.data?.limit,
          total: action?.payload?.data?.total,
        };
      })
      .addCase(getScreeningRules.rejected, (state) => {
        state.loadingRules = false;
        state.rules = [] as any;
      })
      // AI config
      .addCase(getAiConfig.pending, (state) => {
        state.loadingAiConfig = true;
      })
      .addCase(getAiConfig.fulfilled, (state, action) => {
        state.loadingAiConfig = false;
        state.aiConfig = (action?.payload?.data?.data ??
          action?.payload?.data ??
          null) as any;
      })
      .addCase(getAiConfig.rejected, (state) => {
        state.loadingAiConfig = false;
      });
  },
});

export const { resetCandidateScreening } = candidateScreeningReducer.actions;
export default candidateScreeningReducer.reducer;
